import { AFFILIATE_COOKIES } from "../constants/cookiesLifetime";

/**
 * Decodes a referral token
 * @param {string} referralCode - The hybrid referral token (format: randomtokenencodeddata)
 * @returns {Object|null} - Decoded data { exp, ref, svc, isExpired } or null if invalid
 */
export function decodeReferralCode(referralCode) {
  try {
    // Check format: should be a valid string
    if (!referralCode || typeof referralCode !== "string") {
      return null;
    }

    // Validate minimum length (11 chars for random token + some encoded data)
    if (referralCode.length < 15) {
      return null;
    }

    // Extract parts: first 11 chars = random part, rest = data part
    // secrets.token_urlsafe(8) generates 11 characters, not 8
    const randomPart = referralCode.substring(0, 11);
    const dataPart = referralCode.substring(11);

    // Validate parts exist
    if (!randomPart || !dataPart) {
      return null;
    }

    // Decode the data part from standard base64
    // Add padding if needed (backend removes padding for cleaner URLs)
    let standardBase64 = dataPart;
    const paddingNeeded = (4 - (standardBase64.length % 4)) % 4;
    standardBase64 += "=".repeat(paddingNeeded);

    const decodedString = atob(standardBase64);

    // Parse format: "expiration_seconds,referrer_id,service"
    const dataArray = decodedString.split(",");

    if (dataArray.length !== 3) {
      return null;
    }

    const [expSeconds, referrerId, service] = dataArray;

    // Convert expiration to milliseconds and validate
    const expiration = parseInt(expSeconds) * 1000;
    const now = Date.now();
    const isExpired = expiration <= now;

    const result = {
      exp: expiration, // expiration timestamp in ms
      ref: parseInt(referrerId), // referrer_id
      svc: service.trim(), // service (TF/EF)
      isExpired: isExpired,
    };

    return result;
  } catch (error) {
    return null;
  }
}

// Service mapping configuration
const SERVICE_ROUTE_MAPPING = {
  TF: "/tax-filing", // Tax Filing service
  AC: "/sign-up", // Create account 
  // "EF": "/e-filing",    // E-Filing service (if exists)
  // Add more services as needed
};

/**
 * Get the route for a service code
 * @param {string} serviceCode - The service code (e.g., "TF", "EF")
 * @returns {string} - The route for the service
 */
export function getServiceRoute(serviceCode) {
  const route = SERVICE_ROUTE_MAPPING[serviceCode] || "/";
  return route;
}

/**
 * Validates a referral code
 * @param {string} referralCode - The referral code to validate
 * @returns {Object} - { isValid: boolean, error?: string, decoded?: Object }
 */
export function validateReferralCode(referralCode) {
  // Basic validation - check if has value
  if (!referralCode || referralCode.trim() === "") {
    return {
      isValid: false,
      error: "Referral code is empty or invalid",
    };
  }

  // Decode and validate format
  const decoded = decodeReferralCode(referralCode);

  if (!decoded) {
    return {
      isValid: false,
      error: "Invalid referral code format",
    };
  }

  // Check expiration
  if (decoded.isExpired) {
    return {
      isValid: false,
      error: "Referral code has expired",
    };
  }

  const result = {
    isValid: true,
    decoded,
  };

  return result;
}

/**
 * Calculates cookie lifetime for referral tracking
 * @param {string} referralCode - The referral code (for future token lifetime extraction)
 * @returns {number} - Cookie lifetime in days
 */
export function calculateCookieLifetime(referralCode) {

  // Extract token lifetime and compare
  const decoded = decodeReferralCode(referralCode);
  if (decoded && decoded.exp && !decoded.isExpired) {
    const tokenLifetimeMs = decoded.exp - Date.now();
    const tokenLifetimeDays = tokenLifetimeMs / (1000 * 60 * 60 * 24);
    return Math.min(
      AFFILIATE_COOKIES.SERVICE_REFERAL,
      Math.max(1, Math.floor(tokenLifetimeDays))
    );
  }

  // If token is expired or invalid, return minimum lifetime (1 day)
  return 1;
}
